import { z } from "zod";

const decimal = z.string().regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/);
const isoDate = z.iso.date();
const timestamp = z.iso.datetime({ offset: true });

export const payrollCorrectionStatusSchema = z.enum([
  "proposed",
  "approved",
  "posted",
  "paid",
  "superseded",
  "cancelled",
  "failed",
]);

export type PayrollCorrectionStatus = z.infer<typeof payrollCorrectionStatusSchema>;

const payrollCorrectionAllocationSchema = z
  .object({
    id: z.string().min(1),
    allocationKey: z.string().min(1),
    componentId: z.string().min(1),
    signedAmount: decimal,
    recognizedAmount: decimal,
    correctedAmount: decimal,
    outstandingAmount: decimal,
    settledAmount: decimal,
    currency: z.string().regex(/^[A-Z]{3}$/),
    taxTiming: z.string().min(1),
    postingKind: z.enum(["additional_salary", "recovery", "no_op"]),
    postingRef: z.string().min(1),
    status: z.enum(["reserved", "posted", "partially_settled", "settled", "failed"]),
    destinationPeriodStart: isoDate.nullable(),
    additionalSalaryId: z.string().min(1).nullable(),
    recoveryScheduleId: z.string().min(1).nullable(),
    failureReason: z.string().min(1).nullable(),
  })
  .strict();

export const payrollCorrectionSchema = z
  .object({
    id: z.string().min(1),
    membershipId: z.string().min(1),
    daySettlementId: z.string().min(1).nullable(),
    sourceCycleStart: isoDate,
    sourceCycleEnd: isoDate,
    recognitionRevision: z.number().int().positive(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    countryPackVersion: z.string().min(1).nullable(),
    statutoryReviewStatus: z.enum(["pending", "approved", "not_required"]),
    totalDelta: decimal,
    recognizedBaselineAmount: decimal,
    correctedEntitlement: decimal,
    sourceLossCap: decimal.nullable(),
    status: payrollCorrectionStatusSchema,
    approvedByUserId: z.string().min(1).nullable(),
    approvedAt: timestamp.nullable(),
    destinationCycleStart: isoDate.nullable(),
    attemptCount: z.number().int().nonnegative(),
    failureReason: z.string().min(1).nullable(),
    nextAttemptAt: timestamp.nullable(),
    postedAt: timestamp.nullable(),
    settledAt: timestamp.nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
    allocations: z.array(payrollCorrectionAllocationSchema),
  })
  .strict()
  .refine(({ sourceCycleEnd, sourceCycleStart }) => sourceCycleEnd >= sourceCycleStart, {
    message: "The correction source cycle is invalid.",
  });

export type PayrollCorrection = z.infer<typeof payrollCorrectionSchema>;

export const payrollCorrectionListSchema = z
  .object({ rows: z.array(payrollCorrectionSchema), nextCursor: z.string().min(1).nullable() })
  .strict();

export type PayrollCorrectionList = z.infer<typeof payrollCorrectionListSchema>;

export const payrollCorrectionFiltersSchema = z
  .object({
    membershipId: z.string().min(1).optional(),
    status: payrollCorrectionStatusSchema.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .strict();

export type PayrollCorrectionFilters = z.infer<typeof payrollCorrectionFiltersSchema>;
